/**
 * EcosystemContentRenderer: renders an ordered list of authored content
 * blocks.
 *
 * P5 wave 2 (content renderer). Takes a `blocks` array (the published
 * `blocks` payload from GET /api/ecosystem-content/:pageKey), sorts it by
 * `order` ascending, and renders each block via the shared blockRegistry.
 *
 * Forward-compatible: a block whose `type` has no registry entry is skipped
 * (renders nothing) rather than crashing: an older client stays usable when
 * a newer block type appears in authored content. Each block component is
 * itself responsible for degrading gracefully on partial `data`.
 *
 * Layout system (mirrors the platform apex): the hero and ecosystem-join are
 * full-bleed banners; prose, headings, and quotes sit in a narrow readable
 * column; structural blocks that read as card grids (stat rows, and paragraphs
 * whose authored HTML contains a list) use a wider column so they fill the page
 * width like the apex card sections. Consecutive CTA buttons collapse into a
 * single centered button row instead of stacking left in the column.
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { getBlockComponent } from './blockRegistry';
import { getBlockTypeDescriptor } from './editor/blockTypes';
import './Content.css';

// A paragraph whose authored HTML contains a list renders that list as a card
// grid (.content-richtext ul in Content.css), so it gets the wide column.
const PARAGRAPH_LIST_RE = /<(ul|ol)\b/i;

// Block types that are full-bleed (own their own width + background). These
// render without a .content-column wrapper so their backgrounds span the
// full viewport. Carousel blocks use a bg-light full-width section;
// ecosystem-join uses a dark gradient banner.
const FULL_BLEED_TYPES = new Set([
    'hero',
    'ecosystem-join',
    'ecosystem-orgs',
    'recent-orgs',
]);

export default function EcosystemContentRenderer({ blocks }) {
    // Sort by `order` ascending without mutating the prop. A non-array
    // `blocks` (defensive, should not happen) yields an empty list.
    const orderedBlocks = useMemo(() => {
        if (!Array.isArray(blocks)) {
            return [];
        }
        return [...blocks].sort((a, b) => {
            const orderA = typeof a?.order === 'number' ? a.order : 0;
            const orderB = typeof b?.order === 'number' ? b.order : 0;
            return orderA - orderB;
        });
    }, [blocks]);

    // Pre-pass: collapse runs of consecutive `cta-button` blocks into one
    // centered button-row group so a set of CTAs reads as a single action row
    // (like the apex hero's side-by-side CTAs) instead of stacking left, each
    // a full block-gap apart. Every other block stands alone.
    const groups = useMemo(() => {
        const out = [];
        orderedBlocks.forEach((block, index) => {
            if (!block || typeof block !== 'object') {
                return;
            }
            if (block.type === 'cta-button') {
                const last = out[out.length - 1];
                if (last && last.kind === 'cta-group') {
                    last.items.push({ block, index });
                    return;
                }
                out.push({ kind: 'cta-group', items: [{ block, index }] });
                return;
            }
            out.push({ kind: 'single', block, index });
        });
        return out;
    }, [orderedBlocks]);

    const CtaButton = getBlockComponent('cta-button');

    return (
        <div className="ecosystem-content">
            {groups.map((group) => {
                // A run of CTA buttons: one centered, wrapping button row in
                // the wide column.
                if (group.kind === 'cta-group') {
                    const first = group.items[0];
                    const groupKey =
                        first.block._clientId || `cta-group-${first.index}`;
                    if (!CtaButton) {
                        return null;
                    }
                    return (
                        <div
                            className="content-column content-column--wide content-cta-group"
                            key={groupKey}
                        >
                            {group.items.map(({ block, index }) => (
                                <CtaButton
                                    data={block.data || {}}
                                    key={block._clientId || `cta-${index}`}
                                />
                            ))}
                        </div>
                    );
                }

                const { block, index } = group;
                const BlockComponent = getBlockComponent(block.type);
                // Unknown block type: skip silently (forward-compatible).
                if (!BlockComponent) {
                    return null;
                }
                // Prefer the stable `_clientId` the editor mints onto every
                // block (Wave C). The (type, index) fallback covers published
                // rows hydrated straight from the DB which don't carry one.
                const blockKey = block._clientId || `${block.type}-${index}`;
                const rendered = <BlockComponent data={block.data || {}} />;

                if (FULL_BLEED_TYPES.has(block.type)) {
                    return (
                        <React.Fragment key={blockKey}>
                            {rendered}
                        </React.Fragment>
                    );
                }

                // Stat rows, capability-cards, and list-bearing paragraphs
                // read as card grids; they get the wide column. The static
                // `isWide` flag on the descriptor covers always-wide types;
                // the list check handles paragraph's content-based widening.
                const html =
                    block.type === 'paragraph' ? block.data?.html || '' : '';
                const descriptor = getBlockTypeDescriptor(block.type);
                const isWide =
                    (descriptor?.isWide ?? false) ||
                    PARAGRAPH_LIST_RE.test(html);
                const columnClass = [
                    'content-column',
                    isWide && 'content-column--wide',
                    block.type === 'heading' && 'content-column--heading',
                ]
                    .filter(Boolean)
                    .join(' ');

                return (
                    <div className={columnClass} key={blockKey}>
                        {rendered}
                    </div>
                );
            })}
        </div>
    );
}

EcosystemContentRenderer.propTypes = {
    blocks: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            data: PropTypes.object,
            order: PropTypes.number,
            // _clientId is editor-minted; absent on DB-hydrated rows.
            _clientId: PropTypes.string,
        })
    ),
};

EcosystemContentRenderer.defaultProps = {
    blocks: [],
};
